import { useContext, useEffect, useState } from "react";
import styles from "./Placeholder.module.css";
import { Button, Divider } from "@shopify/polaris";
import { BundleTypes } from "../constants";
import BundleInfo from "../CommonFields/BundleInfo";
import Discount from "../CommonFields/Discount";
import { PlaceholderContext, PlaceholderContextType } from "./Placeholder";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import { PlusCircleIcon } from "@shopify/polaris-icons";
import { PlaceholderStepsType, StepsType } from "../Types";
// import { postData } from "../../../utilities/networkCalls";
// import config from "../../../config";
import TextFields from "../CommonFields/TextFields";
import usePlaceholderMixpanelHook from "./usePlaceholderMixpanelHook";
// import { truncateFileName } from "../../../utilities/functions";
import PlaceholderStep from "./PlaceholderStep";
import { DEFAULT_STEP } from "./usePlaceholderHook";
// import PlaceholderFreeGiftStep from "./PlaceholderFreeGiftStep";
import SwatchesSelectorWrapper from "../CommonFields/SwatchesSelectorWrapper";

// const MAX_FILE_SIZE = 1 * 1024 * 1024; // 1MB
// const MAX_FILE_COUNT = 1; // Max allowed number of files
// const ACCEPTED_FILE_TYPES = ["image/jpeg", "image/png", "image/gif"];

const PlaceholderContentForm = () => {
    const context = useContext(PlaceholderContext);
    const { trackBbStep } = usePlaceholderMixpanelHook();
    const {
        fields: {
            bundle_name,
            title,
            description,
            products,
            discount_type,
            discount_value,
            hide_prices_product_card,
            order_discounts,
            product_discounts,
            shipping_discounts,
            footer_total_text,
            footer_text,
            add_to_cart_cta_text,
            add_to_cart_cta_hover_text,
            free_text,
            bundles_design: { default_cta },
            steps,
            // free_gift_step,
            buy_now_cta_text,
            edit_cta_text,
            next_cta_text,
            done_cta_text,
            out_of_stock_text,
            previous_cta_text,
            order_summary_subtotal,
            order_summary_discount,
            discount_options,
            tier_discount_basis,
            pre_selected_products,
            allow_pre_selected_products,
            show_swatches,
            swatchOptions,
            swatchesProductsMap,
            swatchShape,
            swatchSize,
            show_progress_bar,
            offer_name,
        },
        handleNext = () => {},
        dispatchBundleConfig,
        isMobile,
        SwatchesBundleConfigTouchedRef,
        bundleConfig,
    } = context as PlaceholderContextType;
    const [expandStep, setExpandStep] = useState(0);
    const [addedNow, setAddedNow] = useState(false);
    const handleAddStep = () => {
        const totalSteps = steps?.value?.length || 0;
        const updatedArray = steps?.value?.length
            ? [...steps.value, { ...DEFAULT_STEP, step_label: `Choose Step ${totalSteps + 1} Product(s)` }]
            : [{ ...DEFAULT_STEP, step_label: `Step ${totalSteps + 1}` }];
        dispatchBundleConfig({ payload: [...updatedArray], key: "steps" });
        steps.onChange([...updatedArray]);
        trackBbStep({ cta: "Add Step", stepId: totalSteps + 1, ctaType: "Add Step" });
        setExpandStep(totalSteps);
        setAddedNow(true);
        setTimeout(() => {
            setAddedNow(false);
        }, 1500);
    };

    const handleDragEnd = (result: any) => {
        if (!result.destination) return;
        const reorderedSteps = Array.from(steps.value);
        const [movedStep] = reorderedSteps.splice(result.source.index, 1);
        reorderedSteps.splice(result.destination.index, 0, movedStep);
        dispatchBundleConfig({ payload: [...reorderedSteps], key: "steps" });
        trackBbStep({ cta: "Steps Reorder", stepId: result.source.index + 1, ctaType: "Add Step" });
        steps.onChange([...reorderedSteps]);
    };

    const handleEditStep = (
        idx: number,
        keyOrChanges: keyof PlaceholderStepsType | "delete" | "copy" | Record<string, any>,
        value?: any,
    ) => {
        let updatedSteps: PlaceholderStepsType[] = [...steps.value];
        if (keyOrChanges === "delete") {
            updatedSteps = updatedSteps.filter((_, index) => index != idx);
        } else if (keyOrChanges === "copy") {
            const originalStep = updatedSteps[idx];
            const copiedStep: PlaceholderStepsType = {
                ...originalStep,
                step_label: originalStep.step_label + ": Copy",
            };
            updatedSteps.splice(idx + 1, 0, copiedStep);
            setExpandStep(idx + 1);
        } else {
            const updatedStep: PlaceholderStepsType = { ...updatedSteps[idx] };
            if (typeof keyOrChanges === "object" && keyOrChanges !== null) {
                Object.assign(updatedStep, keyOrChanges);
                trackBbStep({ cta: "Edit Step", stepId: idx + 1, ctaType: "Multiple changes" });
            } else {
                const key = keyOrChanges as keyof PlaceholderStepsType;
                if (key === "product_selection") {
                    updatedStep.products = [];
                    updatedStep.collections = [];
                }

                if (key === "step_content_type") {
                    if (value === "free_gift") {
                        updatedStep.render_as_variants = true;
                        updatedStep.show_step_conditions = true;
                        updatedStep.step_conditions = [
                            {
                                basis: "items_count",
                                limit: 1,
                                type: "exact_products",
                                msg: "Exactly 1 product required",
                            },
                        ];
                    } else if (value === "products") {
                        updatedStep.render_as_variants = false;
                        updatedStep.show_step_conditions = false;
                        updatedStep.step_conditions = [
                            {
                                basis: "items_count",
                                limit: 1,
                                type: "min_products",
                                msg: "Minimum 1 product required",
                            },
                        ];
                    }
                }

                trackBbStep({ cta: "Edit Step", stepId: idx + 1, ctaType: key as string });
                (updatedStep as any)[key] = value;
            }
            updatedSteps[idx] = updatedStep;
        }
        dispatchBundleConfig({ payload: [...updatedSteps], key: "steps" });
        steps.onChange([...updatedSteps]);
    };

    // const handleEditFreeGiftStep = <K extends keyof FreeGiftStepType>(key: K | "delete", value?: any) => {
    //     let updatedFgSteps: FreeGiftStepType = { ...free_gift_step.value };
    //     if (key == "delete") {
    //         updatedFgSteps = { ...updatedFgSteps, is_enabled: false };
    //     } else {
    //         trackBbStep({ cta: "Free Gift Step Edit", stepId: -1, ctaType: key });
    //         updatedFgSteps[key] = value;
    //     }
    //     free_gift_step.onChange({ ...updatedFgSteps });
    //     dispatchBundleConfig({ payload: { ...updatedFgSteps }, key: "free_gift_step" });
    // };

    // const handleDropZoneDrop = async (
    //     idx: number,
    //     _dropFiles: File[],
    //     newFiles: File[],
    //     step: "steps" | "free_gift" = "steps",
    // ) => {
    //     const remainingFileCount = 1;
    //     if (newFiles.length > remainingFileCount) {
    //         shopify.toast.show(`You can only upload up to ${MAX_FILE_COUNT} files.`, { isError: true });
    //         return [];
    //     }

    //     const validFiles: File[] = [];
    //     for (const file of newFiles) {
    //         if (file?.name) {
    //             const fileName = truncateFileName(file.name, 20);

    //             if (!ACCEPTED_FILE_TYPES.includes(file.type)) {
    //                 shopify.toast.show(`${fileName} is not a supported file type.`, {
    //                     isError: true,
    //                 });
    //                 await new Promise((resolve) => setTimeout(resolve, 1000));
    //             } else if (file.size > MAX_FILE_SIZE) {
    //                 shopify.toast.show(`${fileName} exceeds the 5MB file size limit.`, { isError: true });
    //                 await new Promise((resolve) => setTimeout(resolve, 1000));
    //             } else {
    //                 validFiles.push(file);
    //             }
    //         }
    //     }

    //     const filesToProcess = validFiles.slice(0, remainingFileCount);

    //     for (const file of filesToProcess) {
    //         // Add a placeholder to show "Uploading..."
    //         const placeholderItem: StepsType["step_icon"] = {
    //             originalSrc: "",
    //             alt: file.name || "icon",
    //             // @ts-expect-error required
    //             mediaContentType: "IMAGE",
    //         };
    //         if (step == "free_gift") {
    //             handleEditFreeGiftStep("step_icon", placeholderItem);
    //         } else {
    //             handleEditStep(idx, "step_icon", placeholderItem);
    //         }

    //         const data = new FormData();
    //         data.append("file", file);

    //         try {
    //             const response = await postData({
    //                 url: `/${config.appName}/upload`,
    //                 payload: data,
    //             });

    //             if (!response || !response.data || !response.data.url) {
    //                 throw new Error("Invalid response format");
    //             }

    //             if (step == "free_gift") {
    //                 handleEditFreeGiftStep("step_icon", {
    //                     ...free_gift_step?.value?.step_icon,
    //                     originalSrc: response.data.url,
    //                 });
    //             } else {
    //                 handleEditStep(idx, "step_icon", { ...steps[idx], originalSrc: response.data.url });
    //             }
    //         } catch (error) {
    //             console.error("Error during file upload:", error);
    //             if (step == "free_gift") {
    //                 handleEditFreeGiftStep("step_icon", null);
    //             } else {
    //                 handleEditStep(idx, "step_icon", null); // Remove the placeholder if upload fails
    //             }
    //             shopify.toast.show("Upload failed! Please try again.", {
    //                 isError: true,
    //             });
    //         }
    //     }
    // };

    useEffect(() => {
        if (
            !pre_selected_products?.value ||
            pre_selected_products?.value?.length == 0 ||
            !allow_pre_selected_products?.value
        )
            return;

        const stepProducts = steps.value.flatMap((s: StepsType) => {
            if (s?.enable_subSteps) {
                return s?.subSteps?.flatMap((ss) => ss.products.map((p) => p.id));
            } else {
                return s.products.map((p) => p.id);
            }
        });

        const stepCollections = steps.value.flatMap((s: StepsType) => {
            if (s?.enable_subSteps) {
                return s?.subSteps?.flatMap((ss) => ss.collections.map((c) => c.id));
            } else {
                return s.collections.map((c) => c.id);
            }
        });

        const newPreSelectedProductsList = pre_selected_products.value.filter((product: any) => {
            const isDirectProduct = stepProducts.includes(product.id);
            // @ts-expect-error hack
            const isInAllowedCollection = product?.collections?.some((c) => stepCollections.includes(c));
            return isDirectProduct || isInAllowedCollection;
        });

        if (newPreSelectedProductsList.length !== pre_selected_products?.value?.length) {
            pre_selected_products.onChange(newPreSelectedProductsList);
            dispatchBundleConfig({
                payload: newPreSelectedProductsList,
                key: "pre_selected_products",
            });
            shopify.toast.show("Only products included in the bundle can be set as preselected", {
                isError: true,
                duration: 1500,
            });
        }
    }, [steps?.value]);

    return (
        <div className={styles.formWrapper}>
            <div style={{ marginBottom: "4px" }}>
                <Divider borderColor="border" />
            </div>
            <div className={styles.collapsibleFormBox}>
                <BundleInfo
                    description={description}
                    title={title}
                    bundle_name={bundle_name}
                    bundleType={BundleTypes.BB_PLACEHOLDER}
                    dispatchBundleConfig={dispatchBundleConfig as any}
                    offer_name={offer_name}
                />

                <div className={styles.dividerStyle}>
                    <Divider borderColor="border" />
                </div>

                <div className={styles.sectionTitleWrapper}>
                    <div>Configure Steps</div>
                </div>

                <div className={styles.stepTabContainer}>
                    {
                        // @ts-expect-error required
                        <DragDropContext onDragEnd={handleDragEnd}>
                            {
                                // @ts-expect-error required
                                <Droppable droppableId="droppable-tabs">
                                    {(provided) => (
                                        <div
                                            {...provided.droppableProps}
                                            ref={provided.innerRef}
                                            className={styles.stepsDroppableContainer}
                                        >
                                            {steps?.value?.map((step: StepsType, index: any) => (
                                                // @ts-expect-error required
                                                <Draggable key={index} draggableId={index.toString()} index={index}>
                                                    {(provided, snapshot) => (
                                                        <div
                                                            ref={provided.innerRef}
                                                            {...provided.draggableProps}
                                                            className={`${styles.stepDraggableItem}`}
                                                        >
                                                            <PlaceholderStep
                                                                provided={provided}
                                                                key={"step" + index}
                                                                step={step}
                                                                index={index}
                                                                snapshot={snapshot}
                                                                // handleDropZoneDrop={(
                                                                //     _dropFiles: any,
                                                                //     acceptedFiles: any,
                                                                // ) =>
                                                                //     handleDropZoneDrop(index, _dropFiles, acceptedFiles)
                                                                // }
                                                                isMobile={isMobile}
                                                                handleEditStep={handleEditStep}
                                                                steps={steps}
                                                                expandStep={expandStep}
                                                                setExpandStep={setExpandStep}
                                                                addedNow={addedNow}
                                                                selected_template_placeholder={
                                                                    bundleConfig?.selected_template_placeholder ??
                                                                    "normal"
                                                                }
                                                            />
                                                        </div>
                                                    )}
                                                </Draggable>
                                            ))}
                                            {provided.placeholder}
                                        </div>
                                    )}
                                </Droppable>
                            }
                        </DragDropContext>
                    }
                    <div className={styles.customAddStepBtn}>
                        <Button onClick={handleAddStep} variant={"secondary"} fullWidth icon={PlusCircleIcon}>
                            Add Step
                        </Button>
                    </div>
                    {/* <PlaceholderFreeGiftStep
                        free_gift_step={free_gift_step}
                        steps={steps}
                        dispatchBundleConfig={dispatchBundleConfig}
                        isMobile={isMobile}
                        handleDropZoneDrop={handleDropZoneDrop}
                        handleEditFreeGiftStep={handleEditFreeGiftStep}
                    /> */}
                </div>

                <div className={styles.dividerStyle}>
                    <Divider borderColor="border" />
                </div>

                <Discount
                    dispatchBundleConfig={dispatchBundleConfig}
                    discount_type={discount_type}
                    discount_value={discount_value}
                    order_discounts={order_discounts}
                    product_discounts={product_discounts}
                    shipping_discounts={shipping_discounts}
                    isMobile={isMobile}
                    bundleType={BundleTypes.BB_PLACEHOLDER}
                    products={products}
                    hide_prices_product_card={hide_prices_product_card}
                    discount_options={discount_options}
                    tier_discount_basis={tier_discount_basis}
                    show_progress_bar={show_progress_bar}
                />

                <div className={styles.dividerStyle}>
                    <Divider borderColor="border" />
                </div>

                <SwatchesSelectorWrapper
                    dispatchBundleConfig={dispatchBundleConfig}
                    show_swatches={show_swatches}
                    swatchOptions={swatchOptions}
                    swatchesProductsMap={swatchesProductsMap}
                    swatchShape={swatchShape}
                    swatchSize={swatchSize}
                    productSource={{
                        type: "specific",
                        value: steps.value.flatMap((s: StepsType) =>
                            s?.enable_subSteps ? s.subSteps.flatMap((ss) => ss.products) : s.products,
                        ),
                    }}
                    bundleType={BundleTypes.BB_PLACEHOLDER}
                    bundleConfig={bundleConfig}
                    SwatchesBundleConfigTouchedRef={SwatchesBundleConfigTouchedRef}
                />

                <div className={styles.dividerStyle}>
                    <Divider borderColor="border" />
                </div>

                <TextFields
                    dispatchBundleConfig={dispatchBundleConfig}
                    bundleType={BundleTypes.BB_PLACEHOLDER}
                    footer_total_text={footer_total_text}
                    footer_text={footer_text}
                    free_text={free_text}
                    add_to_cart_cta_text={add_to_cart_cta_text}
                    add_to_cart_cta_hover_text={add_to_cart_cta_hover_text}
                    default_cta={default_cta}
                    buy_now_cta_text={buy_now_cta_text}
                    edit_cta_text={edit_cta_text}
                    next_cta_text={next_cta_text}
                    previous_cta_text={previous_cta_text}
                    done_cta_text={done_cta_text}
                    order_summary_subtotal={order_summary_subtotal}
                    order_summary_discount={order_summary_discount}
                    out_of_stock_text={out_of_stock_text}
                />
            </div>
            <div className={styles.dividerStyle}>
                <Divider borderColor="border" />
            </div>

            <div className={styles.bottomRowCta}>
                <span></span>
                <Button variant="secondary" onClick={() => handleNext(1, "Design")}>
                    Next
                </Button>
            </div>
        </div>
    );
};
export default PlaceholderContentForm;
